import { ModuleBase, Media, Text, HeadingTag, Button } from '@/components';
import { isColorDark } from '@/utils';
import classNames from 'classnames';

const SplitMediaRow = ({ row, dark }) => {
  const { headingTitle, headingStyle, description, primaryCta, media, mediaPosition } = row || {};
  const isJumbo = headingStyle?.toLowerCase() === 'jumbo';
  const isGotham = headingStyle?.toLowerCase() === 'gotham';
  const mediaLeft = mediaPosition?.toLowerCase() === 'left';

  const headingClasses = classNames(
    'richtext-content color-from-bg mb-4 font-normal not-italic lg:mb-6',
    isGotham
      ? 'font-gotham text-[18px] font-n-light leading-6'
      : isJumbo
        ? 'font-larken text-[32px] leading-tight sm:text-[40px] lg:text-[48px] xl:text-[56px] xl:leading-[60px]'
        : 'font-larken text-2xl leading-7 md:text-desktop-medium md:leading-desktop-medium',
  );

  const heading = headingTitle?.heading && <HeadingTag animate data={headingTitle} className={headingClasses} />;

  return (
    <div
      className={classNames(
        'flex flex-col gap-2 lg:flex-row lg:items-stretch lg:gap-x-[8%]',
        mediaLeft ? 'lg:flex-row' : 'lg:flex-row-reverse',
      )}
    >
      {/* Mobile-only heading (sandwich order). */}
      <div className="lg:hidden">{heading}</div>

      <div className="w-full lg:w-1/2">
        <div className="relative aspect-[4/3] w-full overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[400px] xl:min-h-[500px]">
          <Media
            media={media}
            widths={{ xs: 345, md: 768, lg: 800, xl: 800 }}
            cover={media.autoPlay || (!media.autoPlay && !media.vimeoId && !media.mobileVimeoId && !media.youtubeId)}
            background={media.autoPlay}
          />
        </div>
      </div>

      <div
        className={classNames(
          'flex w-full flex-col justify-center pb-8 lg:w-1/2 lg:max-w-[560px] lg:pt-8',
          mediaLeft ? 'lg:pr-[5%] xl:pr-[6%]' : 'lg:pl-[5%] xl:pl-[6%]',
        )}
      >
        <div className="hidden lg:block">{heading}</div>

        {description && (
          <Text
            dangerouslySetInnerHTML={{ __html: description }}
            className={classNames(
              'richtext-content font-gotham text-small-paragraph font-s-light leading-6 [&_a]:underline [&_p:last-child]:mb-0 [&_p]:mb-4',
              dark ? 'text-white/75' : 'text-black/75',
            )}
          />
        )}

        {primaryCta && (
          <Button className={classNames('btn secondary mt-8 w-fit', !dark ? 'dark' : '')} link={primaryCta} />
        )}
      </div>
    </div>
  );
};

const SplitMediaModule = ({ data }) => {
  const { headingTitle, description, rowSpacing, rows } = data || {};
  const dark = isColorDark(data?.backgroundColour);
  const isLargeSpacing = rowSpacing?.toLowerCase() === 'large';

  return (
    <ModuleBase data={data}>
      <div className="container px-6 py-8 md:px-10">
        <div className="mx-auto max-w-[1600px]">
          {(headingTitle?.heading || description) && (
            <div className="mx-auto mb-16 max-w-[890px] text-center">
              {headingTitle?.heading && (
                <HeadingTag
                  animate
                  data={headingTitle}
                  className="color-from-bg mb-4 font-larken text-[32px] font-normal not-italic leading-tight lg:text-[44px]"
                />
              )}
              {description && (
                <Text
                  dangerouslySetInnerHTML={{ __html: description }}
                  className={classNames(
                    'richtext-content font-gotham text-small-paragraph font-s-light leading-6 [&_a]:underline [&_p:last-child]:mb-0 [&_p]:mb-4',
                    dark ? 'text-white/75' : 'text-black/75',
                  )}
                />
              )}
            </div>
          )}

          {rows?.length > 0 && (
            <div
              className={classNames(
                'flex flex-col',
                isLargeSpacing ? 'gap-y-20 lg:gap-y-[252px]' : 'gap-y-16 lg:gap-y-[100px]',
              )}
            >
              {rows.map((row, index) => (
                <SplitMediaRow key={index} row={row} dark={dark} />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModuleBase>
  );
};

export default SplitMediaModule;
